import GjsEditor from "@grapesjs/react";
import grapesjs, { Editor } from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import newsletterPreset from "grapesjs-preset-newsletter";
import {
  ChevronDown,
  Copy,
  Eye,
  FilePlus,
  FileText,
  Save,
  Code2,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "@/hooks/use-toast";
import { useTemplates } from "../hooks/use-templates";
import { PreviewWithDataDialog } from "./preview-with-data-dialog";
import { TemplateList } from "./template-list";
import { TemplateSaveDialog } from "./template-save-dialog";

type SaveMode = "create" | "rename";

function wrapHtml(html: string, css: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Email</title>
<style>${css}</style>
</head>
<body>
${html}
</body>
</html>`;
}

export function EmailBuilderTab() {
  const { templates, save, remove, load } = useTemplates();
  const editorRef = useRef<Editor | null>(null);

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeName, setActiveName] = useState<string | null>(null);
  const [saveDialog, setSaveDialog] = useState<{
    open: boolean;
    mode: SaveMode;
  }>({ open: false, mode: "create" });
  const [exportContent, setExportContent] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{
    html: string;
    css: string;
  } | null>(null);

  const handleEditor = useCallback((editor: Editor) => {
    editorRef.current = editor;
  }, []);

  const handleNew = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.DomComponents.clear();
    editor.CssComposer.clear();
    setActiveId(null);
    setActiveName(null);
  }, []);

  const handleSelectTemplate = useCallback(
    (id: string) => {
      const editor = editorRef.current;
      const record = load(id);
      if (!editor || !record) return;
      editor.loadProjectData(record.projectData as Parameters<Editor["loadProjectData"]>[0]);
      setActiveId(record.id);
      setActiveName(record.name);
    },
    [load],
  );

  const handleSaveClick = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (activeId && activeName) {
      const projectData = editor.getProjectData();
      save({ id: activeId, name: activeName, projectData });
      return;
    }

    setSaveDialog({ open: true, mode: "create" });
  }, [activeId, activeName, save]);

  const handleSaveAsNew = useCallback(() => {
    setSaveDialog({ open: true, mode: "rename" });
  }, []);

  const handleDialogSubmit = useCallback(
    (name: string) => {
      const editor = editorRef.current;
      if (!editor) return;
      const projectData = editor.getProjectData();
      const saved = save({ name, projectData });
      setActiveId(saved.id);
      setActiveName(saved.name);
    },
    [save],
  );

  const handleDelete = useCallback(
    (id: string) => {
      remove(id);
      if (id === activeId) {
        setActiveId(null);
        setActiveName(null);
      }
    },
    [remove, activeId],
  );

  const handleExport = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const html = editor.getHtml();
    const css = editor.getCss() ?? "";
    setExportContent(wrapHtml(html, css));
  }, []);

  const handlePreviewWithData = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    setPreviewData({
      html: editor.getHtml(),
      css: editor.getCss() ?? "",
    });
  }, []);

  const handleCopyExport = useCallback(async () => {
    if (!exportContent) return;
    try {
      await navigator.clipboard.writeText(exportContent);
      toast({ title: "HTML copied to clipboard" });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the text and copy manually.",
        variant: "destructive",
      });
    }
  }, [exportContent]);

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-white rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap">
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              {activeName ?? "Untitled"}
              <ChevronDown className="h-4 w-4 ml-2 opacity-60" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[280px] p-2">
            <div className="text-xs font-medium text-muted-foreground px-1 pb-2">
              Saved templates
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <TemplateList
                templates={templates}
                activeId={activeId}
                onSelect={handleSelectTemplate}
                onDelete={handleDelete}
              />
            </div>
          </PopoverContent>
        </Popover>

        <div className="flex-1" />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleNew}
        >
          <FilePlus className="h-4 w-4 mr-2" />
          New
        </Button>
        <Button type="button" size="sm" onClick={handleSaveClick}>
          <Save className="h-4 w-4 mr-2" />
          {activeId ? "Save" : "Save…"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleSaveAsNew}
          disabled={!activeId}
        >
          Save as new
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handlePreviewWithData}
        >
          <Eye className="h-4 w-4 mr-2" />
          Preview
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleExport}
        >
          <Code2 className="h-4 w-4 mr-2" />
          Export HTML
        </Button>
      </div>

      <div className="bg-white rounded-lg overflow-hidden">
        <GjsEditor
          grapesjs={grapesjs}
          options={{
            height: "calc(100vh - 180px)",
            storageManager: false,
            plugins: [newsletterPreset],
          }}
          onEditor={handleEditor}
        />
      </div>

      <TemplateSaveDialog
        open={saveDialog.open}
        mode={saveDialog.mode}
        initialName={
          saveDialog.mode === "rename" && activeName
            ? `${activeName} (copy)`
            : undefined
        }
        onOpenChange={(open) =>
          setSaveDialog((prev) => ({ ...prev, open }))
        }
        onSubmit={handleDialogSubmit}
      />

      <PreviewWithDataDialog
        open={previewData !== null}
        html={previewData?.html ?? ""}
        css={previewData?.css ?? ""}
        onOpenChange={(open) => !open && setPreviewData(null)}
      />

      <Dialog
        open={exportContent !== null}
        onOpenChange={(open) => !open && setExportContent(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Exported HTML</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopyExport}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
          </div>
          <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted p-3 text-xs">
            {exportContent}
          </pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
